import { ConstraintRule } from '../constraint-rule';
import {
  ActionContextType,
  BookingParams,
  BookingPosition,
  ConstraintSummary,
  StockSourceType,
  StockTargetType,
} from '../../types';

/**
 * Warnt bei Service-Buchung KFZ → Maschine, wenn der Artikel auch im
 * zugeordneten Bufferstock liegt (Befüllung von dort wäre evtl. sinnvoller).
 */
export class RuleServiceArticleNotOnBufferstock extends ConstraintRule {
  /**
   * Greift nur bei aktivem Bufferstock und Fahrzeug für eine Service-Buchung von KFZ zu Maschine.
   * @param position Zu prüfende Buchungsposition (nicht verwendet).
   * @param params Buchungsparameter, aus deren Kontext Systemzustand und Buchungsrichtung gelesen werden.
   * @returns True, wenn die Buchung eine Service-Buchung KFZ zu Maschine bei aktivem Bufferstock und Fahrzeug ist.
   */
  public async needsCheck(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<boolean> {
    const { booking, system } = params.context;
    return (
      system.bufferstockActive &&
      system.carActive &&
      booking.actionContext === ActionContextType.SERVICE &&
      booking.sourceType === StockSourceType.CAR &&
      booking.targetType === StockTargetType.MACHINE
    );
  }

  /**
   * Warnt, wenn der Artikel auch im der Zielmaschine zugeordneten Bufferstock mit Menge grösser null liegt.
   * @param position Zu prüfende Buchungsposition, deren Artikel gesucht wird.
   * @param params Buchungsparameter mit Kontext (Ziel-Maschinen-Id) und Maschinen-Adapter.
   * @returns Nicht erfüllt (Warnung), wenn der Artikel auf dem Bufferstock vorhanden ist, sonst erfüllt.
   */
  public async check(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<ConstraintSummary> {
    const { context, adapters } = params;
    const { booking } = context;
    const machineId = booking.targetId;
    if (!machineId) {
      return this.satisfied();
    }
    const bufferstock = await adapters.machine.findBufferstockLocal(
      machineId
    );
    if (!bufferstock) {
      return this.satisfied();
    }
    const hit = bufferstock.positions.some(
      p => p.article_id === position.article_id && p.amount > 0
    );
    if (hit) {
      return this.unsatisfied(
        $localize`Artikel ${position.article_number} ist auch auf dem Bufferstock der Machine vorhanden`
      );
    }
    return this.satisfied();
  }
}
